import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import Card, { CardRow } from './Card';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

jest.mock('components/styleguide/button/Button', () => ({
  __esModule: true,
  default: ({ children, onClick, 'aria-label': ariaLabel, iconLeft }: any) => (
    <button onClick={onClick} aria-label={ariaLabel} data-icon={iconLeft}>
      {children}
    </button>
  ),
}));

jest.mock('components/styleguide/tag/Tag', () => ({
  __esModule: true,
  default: ({ children, variant }: any) => <span data-variant={variant}>{children}</span>,
}));

describe('Card', () => {
  it('renders title', () => {
    render(<Card title="Experience" />);
    expect(screen.getByText('Experience')).toBeInTheDocument();
  });

  it('renders children', () => {
    render(<Card title="Skills"><p>React, TypeScript</p></Card>);
    expect(screen.getByText('React, TypeScript')).toBeInTheDocument();
  });

  it('renders icon when iconName is provided', () => {
    const { container } = render(<Card title="Experience" iconName="briefcaseBusiness" />);
    expect(container.querySelector('[data-icon="briefcaseBusiness"]')).toBeInTheDocument();
  });

  it('renders tag when provided', () => {
    render(<Card title="Skills" tag={{ variant: 'success', children: 'Complete' }} />);
    expect(screen.getByText('Complete')).toBeInTheDocument();
  });

  it('applies custom className', () => {
    const { container } = render(<Card title="Test" className="custom" />);
    expect(container.firstChild).toHaveClass('custom');
  });

  describe('add button', () => {
    it('renders add button when onAdd and no children', () => {
      const onAdd = jest.fn();
      render(<Card title="Experience" onAdd={onAdd} />);
      expect(screen.getByLabelText('Add experience')).toBeInTheDocument();
    });

    it('renders add button when allowMultiple even with children', () => {
      const onAdd = jest.fn();
      render(<Card title="Skills" onAdd={onAdd} allowMultiple><p>React</p></Card>);
      expect(screen.getByLabelText('Add skills')).toBeInTheDocument();
    });

    it('does not render add button when children exist and not allowMultiple', () => {
      render(<Card title="Skills" onAdd={jest.fn()}><p>React</p></Card>);
      expect(screen.queryByLabelText('Add skills')).not.toBeInTheDocument();
    });

    it('calls onAdd when clicked', () => {
      const onAdd = jest.fn();
      render(<Card title="Experience" onAdd={onAdd} />);
      fireEvent.click(screen.getByLabelText('Add experience'));
      expect(onAdd).toHaveBeenCalledTimes(1);
    });
  });

  describe('edit button', () => {
    it('renders edit button when onEdit and children and not allowMultiple', () => {
      render(<Card title="Bio" onEdit={jest.fn()}><p>Content</p></Card>);
      expect(screen.getByLabelText('Edit bio')).toBeInTheDocument();
    });

    it('does not render edit button when allowMultiple', () => {
      render(<Card title="Bio" onEdit={jest.fn()} allowMultiple><p>Content</p></Card>);
      expect(screen.queryByLabelText('Edit bio')).not.toBeInTheDocument();
    });

    it('does not render edit button when no children', () => {
      render(<Card title="Bio" onEdit={jest.fn()} />);
      expect(screen.queryByLabelText('Edit bio')).not.toBeInTheDocument();
    });

    it('calls onEdit when clicked', () => {
      const onEdit = jest.fn();
      render(<Card title="Bio" onEdit={onEdit}><p>Content</p></Card>);
      fireEvent.click(screen.getByLabelText('Edit bio'));
      expect(onEdit).toHaveBeenCalledTimes(1);
    });
  });

  describe('accessibility', () => {
    it('renders as a section element', () => {
      const { container } = render(<Card title="Experience" />);
      expect(container.querySelector('section')).toBeInTheDocument();
    });

    it('has aria-label matching the title', () => {
      render(<Card title="Experience" />);
      expect(screen.getByRole('region', { name: 'Experience' })).toBeInTheDocument();
    });

    it('marks header icon as aria-hidden', () => {
      const { container } = render(<Card title="Test" iconName="user" />);
      expect(container.querySelector('[data-icon="user"]')).toHaveAttribute('aria-hidden', 'true');
    });
  });
});

describe('CardRow', () => {
  it('renders children', () => {
    render(<CardRow>Row content</CardRow>);
    expect(screen.getByText('Row content')).toBeInTheDocument();
  });

  it('renders edit button when onEdit is provided', () => {
    render(<CardRow onEdit={jest.fn()} label="experience">Row content</CardRow>);
    expect(screen.getByLabelText('Edit experience')).toBeInTheDocument();
  });

  it('renders default edit label when no label prop', () => {
    render(<CardRow onEdit={jest.fn()}>Row content</CardRow>);
    expect(screen.getByLabelText('Edit')).toBeInTheDocument();
  });

  it('does not render edit button when no onEdit', () => {
    render(<CardRow>Row content</CardRow>);
    expect(screen.queryByRole('button')).not.toBeInTheDocument();
  });

  it('calls onEdit when clicked', () => {
    const onEdit = jest.fn();
    render(<CardRow onEdit={onEdit}>Row content</CardRow>);
    fireEvent.click(screen.getByLabelText('Edit'));
    expect(onEdit).toHaveBeenCalledTimes(1);
  });
});
